import Decimal from "decimal.js";
import type { Platform, PricingProject, PricingSettings, QuoteStatus, WbsItem } from "./types";
import { PLATFORMS } from "./types";

Decimal.set({ precision: 24, rounding: Decimal.ROUND_HALF_UP });

const decimal = (value: Decimal.Value) => new Decimal(value || 0);
const number = (value: Decimal) => value.toNumber();

export type WbsCalculation = {
  item: WbsItem;
  baseDays: number;
  fullDays: number;
  platformFactor: number;
  complexityFactor: number;
  specialFactorRaw: number;
  actualDays: number;
  billableDays: number;
  laborFee: number;
  valid: boolean;
  specialFactorExceeded: boolean;
};

export type PlatformSummary = {
  platform: Platform;
  actualDays: number;
  billableDays: number;
  laborFee: number;
};

export type PaymentMilestone = {
  name: string;
  rate: number;
  amount: number;
  condition: string;
};

export type QuoteCalculation = {
  rows: WbsCalculation[];
  platformSummaries: PlatformSummary[];
  actualDays: number;
  billableDays: number;
  adjustedLabor: number;
  reserveRate: number;
  reserveAmount: number;
  addonCost: number;
  addonCustomerAmount: number;
  standardUntaxed: number;
  discountAmount: number;
  discountedUntaxed: number;
  pureLaborIncome: number;
  actualDayRate: number | null;
  taxAmount: number;
  customerTotal: number;
  discoveryUntaxed: number;
  discoveryTax: number;
  discoveryTotal: number;
  maintenanceBasic: number;
  maintenanceStandard: number;
  status: QuoteStatus;
  canPrint: boolean;
  statusMessage: string;
  paymentMilestones: PaymentMilestone[];
};

export function roundToHundreds(value: Decimal.Value): number {
  return number(decimal(value).dividedBy(100).toDecimalPlaces(0).times(100));
}

export function calculateWbsItem(item: WbsItem, settings: PricingSettings): WbsCalculation {
  const quickDays = item.quickSize === "手动" ? item.inputDays : settings.quickSizeDays[item.quickSize] ?? 0;
  const baseDays = decimal(quickDays);
  const fullDays = item.estimateBasis === "编码人日" ? baseDays.dividedBy(settings.codingShare) : baseDays;
  const platformFactor = decimal(settings.platformCoefficients[item.platform] ?? 1);
  const complexityFactor = decimal(settings.complexityCoefficients[item.complexity] ?? 1);
  const specialFactorRaw = item.deliveryFactors.reduce(
    (product, factor) => product.times(settings.deliveryCoefficients[factor] ?? 1),
    decimal(1),
  );
  const actualDays = fullDays.times(platformFactor).times(complexityFactor);
  const billableDays = actualDays.times(specialFactorRaw);
  const laborFee = billableDays.times(settings.dayRate);
  const valid = item.module.trim().length > 0 && item.feature.trim().length > 0 && baseDays.gt(0);

  return {
    item,
    baseDays: number(baseDays),
    fullDays: valid ? number(fullDays) : 0,
    platformFactor: number(platformFactor),
    complexityFactor: number(complexityFactor),
    specialFactorRaw: number(specialFactorRaw),
    actualDays: valid ? number(actualDays) : 0,
    billableDays: valid ? number(billableDays) : 0,
    laborFee: valid ? number(laborFee) : 0,
    valid,
    specialFactorExceeded: valid && specialFactorRaw.gt(settings.specialFactorCap),
  };
}

function calculatePayments(total: number): PaymentMilestone[] {
  const rates = total <= 10000 ? [0.5, 0, 0, 0.5] : total <= 50000 ? [0.4, 0, 0.3, 0.3] : [0.3, 0.3, 0.3, 0.1];
  const names = ["启动款", "中期款", "测试版款", "验收款"];
  const conditions = ["合同确认后，项目启动前", "中期里程碑确认后", "测试版本交付后", "最终验收或交付前"];
  return rates
    .map((rate, index) => ({ name: names[index], rate, amount: Math.round(total * rate), condition: conditions[index] }))
    .filter((milestone) => milestone.rate > 0);
}

export function calculateQuote(project: PricingProject, settings: PricingSettings): QuoteCalculation {
  const rows = project.wbsItems.map((item) => calculateWbsItem(item, settings));
  const validRows = rows.filter((row) => row.valid);
  const actualDays = validRows.reduce((sum, row) => sum.plus(row.actualDays), decimal(0));
  const billableDays = validRows.reduce((sum, row) => sum.plus(row.billableDays), decimal(0));
  const adjustedLabor = validRows.reduce((sum, row) => sum.plus(row.laborFee), decimal(0));
  const reserveRate = project.demandStatus === "清晰" ? settings.reserveClear : project.demandStatus === "少量不确定" ? settings.reserveUncertain : 0;
  const reserveAmount = adjustedLabor.times(reserveRate);
  const includedAddons = project.addonItems.filter((item) => item.included);
  const addonCost = includedAddons.reduce((sum, item) => sum.plus(item.costAmount), decimal(0));
  const addonCustomerAmount = includedAddons.reduce((sum, item) => sum.plus(item.customerAmount), decimal(0));
  const computedUntaxed = adjustedLabor.plus(reserveAmount).plus(addonCustomerAmount);
  const standardUntaxed = Decimal.max(settings.fixedMinimum, computedUntaxed);
  const discountedUntaxed = Decimal.max(settings.fixedMinimum, standardUntaxed.times(decimal(1).minus(project.discountRate)));
  const discountAmount = standardUntaxed.minus(discountedUntaxed);
  const pureLaborIncome = discountedUntaxed.minus(addonCost);
  const actualDayRate = actualDays.gt(0) ? pureLaborIncome.dividedBy(actualDays) : null;
  const taxAmount = discountedUntaxed.times(project.taxRate);
  const customerTotal = roundToHundreds(discountedUntaxed.plus(taxAmount));
  const discoveryUntaxed = decimal(project.discoveryDays).times(settings.dayRate);
  const discoveryTax = discoveryUntaxed.times(project.taxRate);
  const discoveryTotal = roundToHundreds(discoveryUntaxed.plus(discoveryTax));
  const developmentFee = adjustedLabor.plus(reserveAmount).times(decimal(1).minus(project.discountRate));
  const maintenanceBasic = number(Decimal.max(settings.maintenanceBasicMinimum, developmentFee.times(settings.maintenanceBasicRate)));
  const maintenanceStandard = number(Decimal.max(settings.maintenanceStandardMinimum, developmentFee.times(settings.maintenanceStandardRate)));
  const specialExceeded = validRows.some((row) => row.specialFactorExceeded);
  const splitRequired = actualDays.gt(settings.splitDaysThreshold) || discountedUntaxed.gt(settings.splitAmountThreshold);

  let status: QuoteStatus;
  let statusMessage: string;
  if (project.demandStatus === "不清晰") {
    status = "仅需求梳理";
    statusMessage = `当前只生成${project.discoveryDays}个人日的需求梳理报价。`;
  } else if (validRows.length === 0) {
    status = "缺少工作量";
    statusMessage = "至少填写一个包含模块、功能和人日的WBS项目。";
  } else if (specialExceeded) {
    status = "需调整交付";
    statusMessage = `特殊交付系数超过${settings.specialFactorCap.toFixed(2)}，请拆分阶段或改按实际工时结算。`;
  } else if (splitRequired) {
    status = "需拆分阶段";
    statusMessage = `项目超过${settings.splitDaysThreshold}人日或${settings.splitAmountThreshold.toLocaleString("zh-CN")}元，应拆分MVP与后续阶段。`;
  } else if (actualDayRate !== null && actualDayRate.lt(settings.redlineRate)) {
    status = "禁止报价";
    statusMessage = `实际人日单价低于内部红线¥${settings.redlineRate.toLocaleString("zh-CN")}。`;
  } else {
    status = "可报价";
    statusMessage = "通过内部核价，可以生成客户报价。";
  }

  const platformSummaries = PLATFORMS.map((platform) => {
    const platformRows = validRows.filter((row) => row.item.platform === platform);
    return {
      platform,
      actualDays: platformRows.reduce((sum, row) => sum + row.actualDays, 0),
      billableDays: platformRows.reduce((sum, row) => sum + row.billableDays, 0),
      laborFee: platformRows.reduce((sum, row) => sum + row.laborFee, 0),
    };
  }).filter((summary) => summary.actualDays > 0);

  const printableTotal = status === "仅需求梳理" ? discoveryTotal : customerTotal;
  return {
    rows,
    platformSummaries,
    actualDays: number(actualDays),
    billableDays: number(billableDays),
    adjustedLabor: number(adjustedLabor),
    reserveRate,
    reserveAmount: number(reserveAmount),
    addonCost: number(addonCost),
    addonCustomerAmount: number(addonCustomerAmount),
    standardUntaxed: number(standardUntaxed),
    discountAmount: number(discountAmount),
    discountedUntaxed: number(discountedUntaxed),
    pureLaborIncome: number(pureLaborIncome),
    actualDayRate: actualDayRate ? number(actualDayRate) : null,
    taxAmount: number(taxAmount),
    customerTotal,
    discoveryUntaxed: number(discoveryUntaxed),
    discoveryTax: number(discoveryTax),
    discoveryTotal,
    maintenanceBasic,
    maintenanceStandard,
    status,
    canPrint: status === "可报价" || status === "仅需求梳理",
    statusMessage,
    paymentMilestones: calculatePayments(printableTotal),
  };
}

export function calculateMonthlySupport(
  usedDays: number,
  extraHours: number,
  specialFactor: number,
  settings: PricingSettings,
) {
  const factor = decimal(specialFactor);
  const remainingDays = Decimal.max(decimal(settings.monthlyIncludedDays).minus(usedDays), 0);
  const excessDays = Decimal.max(decimal(usedDays).minus(settings.monthlyIncludedDays), 0);
  const excessFee = excessDays.times(settings.monthlyExcessDayRate).times(factor);
  const hourlyFee = decimal(extraHours).times(settings.hourlyRate).times(factor);
  return {
    remainingDays: number(remainingDays),
    excessDays: number(excessDays),
    excessFee: number(excessFee),
    hourlyFee: number(hourlyFee),
    total: number(decimal(settings.monthlyFee).plus(excessFee).plus(hourlyFee)),
  };
}

export const currency = (value: number) =>
  new Intl.NumberFormat("zh-CN", { style: "currency", currency: "CNY", maximumFractionDigits: 0 }).format(value);

export const numberOne = (value: number) => new Intl.NumberFormat("zh-CN", { minimumFractionDigits: 1, maximumFractionDigits: 1 }).format(value);
